import { commonPasswords } from '@/app/constants/commonPasswords';
import { appClientConfig } from '@/app/utils/appClientConfig';
import { RedirectionPreAction, getSignInUrlServerSide } from '@/app/utils/urlHelper';
import { CheckboxInputProps, RegisterFormProps } from '@/modules/RegistrationFlow/types';
import { WhitepaperSlugs, whitepapers } from '@/modules/Whitepapers/whitepapers';
import { capitaliseFirstChar } from '@/utils/capitaliseFirstChar';
import yup from '@/utils/customisedYup';
import isEmptyProperties from '@/utils/isEmptyProperties';
import { sendBasicInfo } from '@/utils/useRequest';
import { yupResolver } from '@hookform/resolvers';
import landingBg from 'assets/landingBg.png';
import { PATH } from 'constants/path';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { ReactNode, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { debounceCheckEmailStatus } from 'utils/checkEmailStatus';
import { useAnalytics } from 'utils/useAnalytics';

import {
  Button,
  Checkbox,
  FormControl,
  FormGroup,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

import { BackgroundBannerImage } from 'components/BackgroundBannerImage/BackgroundBannerImage';
import { ImageContainer } from 'components/Image';
import { PhoneNumber, phoneNumberSchema } from 'components/PhoneNumber';
import { ProgressBarLayout } from 'components/ProgressBarLayout';

import styles from './RegisterBasicInfo.module.scss';

const isLive = appClientConfig.isLive;
const privacyPolicyUrl = appClientConfig.privacyPolicyUrl;

export const defaultOverlayCenteredText = ["Together, let's", 'open new doors', 'for you.'];

const commonPasswordHint =
  'Your password is unsafe because it is too common. Choose a more unique password.';
const passwordLengthHint = 'Min length is 8.';
export const emailLengthHint = 'Max length is 80.';
export const firstNameLengthHint = 'Max length is 40.';
export const lastNameLengthHint = 'Max length is 80.';

export interface BasicInfoInputs {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  agreeTerms: boolean;
}

const registerSchema = yup.object().shape({
  email: yup.string().trim().max(80, emailLengthHint).required().emailValidation(),
  firstName: yup.string().trim().max(40, firstNameLengthHint).required(),
  lastName: yup.string().trim().max(80, lastNameLengthHint).required(),
  phone: phoneNumberSchema,
  password: yup
    .string()
    .trim()
    .min(8, passwordLengthHint)
    .notInDictionary(commonPasswords, commonPasswordHint)
    .notContainsName()
    .required(),
});

let hasSubmitted = false;
export interface BasicInfoProps {
  onContinue: (KeypairProps) => void;
  formData: RegisterFormProps;
  preAction: RedirectionPreAction | null;
  // Where the user came from, and should be redirected to if they leave the page
  previousRoute: string | null;
  whitepaperSlug: WhitepaperSlugs | null;
}

const NOMAN_ASIF = 'noman.asif@medenterprises.com';
// TODO: potentially have this be env specific and in parameter store
const MEMORIAM_URL = 'https://profile.medworld.com/memoriam/noman';

const RegisterBasicInfo = ({
  onContinue,
  formData,
  preAction,
  previousRoute,
  whitepaperSlug,
}: BasicInfoProps) => {
  const track = useAnalytics();
  const router = useRouter();
  const signInRedirectUrl = getSignInUrlServerSide(previousRoute, preAction);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const defaultValues = {
    ...formData,
    phone: `${formData.phoneArea}${formData.phone}`,
    agreeTerms: false,
  };
  const { handleSubmit, errors, getValues, register, control, setError, watch } =
    useForm<BasicInfoInputs>({
      resolver: yupResolver(registerSchema),
      reValidateMode: 'onChange',
      mode: 'onChange',
      defaultValues,
    });
  const watchAgreeTerms = watch('agreeTerms');
  const changes = useWatch({
    control,
    name: 'email',
  });

  useEffect(() => {
    const handleEmailCheck = emailHasBeenUsed => {
      emailHasBeenUsed && setError('email', { types: { duplicated: true } });
    };
    changes && !errors.email && debounceCheckEmailStatus(changes, handleEmailCheck);
  }, [changes]);

  useEffect(() => {
    hasSubmitted = false;
    return () => {
      if (!hasSubmitted) {
        const values = getValues();
        track('user left basic info flow page', {
          first_name_entered: values.firstName?.length > 0,
          last_name_entered: values.lastName?.length > 0,
          email_entered: values.email?.length > 0,
          password_entered: values.password?.length > 0,
          phone_entered: values.phone?.length > 0,
          agree_terms_entered: values.agreeTerms,
        });
      }
    };
  }, []);

  const handleClickPwEye = () => setShowPassword(!showPassword);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const onSubmit = async data => {
    const initialData: {
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
      gaClientId?: string;
    } = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
    };

    if (isLive) {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const clientId = ga.getAll()[0].get('clientId');
        if (clientId) {
          initialData.gaClientId = clientId;
        }
      } catch (err) {
        console.error('failed to add GA client id', err);
      }
    }

    await sendBasicInfo(initialData);
    hasSubmitted = true;
    const { agreeTerms, ...rest } = data;
    onContinue(rest);
  };

  const getBasicInfoTitleWithUniversalLogin = (): ReactNode => {
    const signInRedirectUrl = getSignInUrlServerSide(previousRoute, preAction);
    let mainTitleText = '';
    switch (preAction) {
      case 'enquire_job':
        mainTitleText =
          'To enquire about this job, you’ll first need to register to create an account or';
        break;
      case 'save_job':
        mainTitleText =
          'To save this job listing, you’ll first need to register to create an account or';
        break;
      case 'create_job_alert':
        mainTitleText =
          'To save this job alert, you’ll first need to register to create an account or';
        break;
      case 'whitepaper':
        mainTitleText =
          'To access this resource, you’ll first need to register to create an account or';
        break;
      default:
        mainTitleText = 'Register for an account, or';
    }
    return (
      <span data-testid="mainTitle_ul">
        {mainTitleText}{' '}
        <a data-testid="mainTitle_signup" href={signInRedirectUrl}>
          sign in
        </a>
        .
      </span>
    );
  };

  const values = getValues();
  const disabledSubmit =
    Object.keys(errors).length > 0 || isEmptyProperties(values) || !watchAgreeTerms;
  return (
    <>
      <div className={styles.consistentSpacing}></div>
      <ProgressBarLayout
        divide
        leftContent={
          <ImageContainer
            height={903}
            width={593}
            img={landingBg}
            overlayCenteredText={
              whitepaperSlug
                ? whitepapers.get(whitepaperSlug)?.textOverlay || defaultOverlayCenteredText
                : defaultOverlayCenteredText
            }
          />
        }
      >
        <div className={styles.formContainer}>
          <div className={styles.bannerContainer}>
            <div className={styles.banner}>
              <BackgroundBannerImage />
            </div>
            <>
              <div className={styles.smTitle__ul}>Welcome to Medrecruit</div>
              <Typography variant="h3" component="h1" className={styles.invisible}>
                Let's get you on board
              </Typography>
              <div className={styles.mainTitle__ul}>{getBasicInfoTitleWithUniversalLogin()}</div>
            </>
          </div>
          <form className={styles.formContent} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.nameInputs}>
              <Controller
                name="firstName"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <TextField
                    name="firstName"
                    label="First Name"
                    inputProps={{ 'data-testid': 'first-name' }}
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    onBlur={onBlur}
                    onChange={e => {
                      const val = capitaliseFirstChar(e.target.value);
                      onChange(val);
                    }}
                    value={value || ''}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <TextField
                    name="lastName"
                    label="Last Name"
                    inputProps={{ 'data-testid': 'last-name' }}
                    fullWidth
                    onBlur={onBlur}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    onChange={e => {
                      const val = capitaliseFirstChar(e.target.value);
                      onChange(val);
                    }}
                    value={value || ''}
                  />
                )}
              />
            </div>
            <div className={styles.accountInputs}>
              <Controller
                name="email"
                control={control}
                render={({ onChange }) => (
                  <TextField
                    className={styles.input}
                    name="email"
                    label="Email"
                    inputProps={{ 'data-testid': 'register-email' }}
                    autoComplete="username email"
                    inputRef={register}
                    onChange={e => {
                      onChange(e);
                      if (e.target.value === NOMAN_ASIF) {
                        // trigger Noman's easter egg
                        router.push(MEMORIAM_URL);
                        return;
                      }
                    }}
                    fullWidth
                    error={!!errors.email}
                    helperText={
                      (errors.email?.types?.duplicated && (
                        <>
                          {'Email has already been registered, please sign in '}
                          <Link prefetch={false} href={signInRedirectUrl} as={signInRedirectUrl}>
                            here
                          </Link>
                        </>
                      )) ||
                      errors.email?.message
                    }
                    defaultValue={formData?.email}
                  />
                )}
              />

              <FormGroup className={styles.input}>
                <FormControl fullWidth error={!!errors.password}>
                  <InputLabel htmlFor="standard-adornment-password">Password</InputLabel>
                  {
                    <Controller
                      name="password"
                      control={control}
                      render={({ onChange }) => (
                        <Input
                          id="standard-adornment-password"
                          inputProps={{ 'data-testid': 'register-password' }}
                          autoComplete="new-password"
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          aria-describedby="password"
                          inputRef={register}
                          onChange={onChange}
                          fullWidth
                          error={!!errors.password}
                          defaultValue={formData?.password}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickPwEye}
                                onMouseDown={handleMouseDownPassword}
                              >
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                      )}
                    />
                  }
                  <FormHelperText data-testid="password-error-msg">
                    {errors.password?.message}
                  </FormHelperText>
                </FormControl>
              </FormGroup>

              <FormGroup row className={styles.input}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ onChange }) => (
                    <PhoneNumber
                      phoneNumber={formData.phone}
                      area={formData.phoneArea}
                      error={errors.phone?.message}
                      onChange={({ mixPhone }) => {
                        onChange(mixPhone);
                      }}
                    />
                  )}
                />
              </FormGroup>
              <div className={styles.termsContainer}>
                <Controller
                  name="agreeTerms"
                  control={control}
                  render={({ onChange, value }) => (
                    <Checkbox
                      checked={value}
                      color="primary"
                      onChange={() => onChange(!value)}
                      inputProps={
                        {
                          'aria-label': 'secondary checkbox',
                          className: styles.checkbox__input,
                          'data-testid': 'agree-terms',
                        } as CheckboxInputProps
                      }
                    />
                  )}
                />

                <span className={styles.terms}>
                  I agree to the{' '}
                  <Link
                    prefetch={false}
                    href={PATH.TERMS_AND_CONDITIONS}
                    as={PATH.TERMS_AND_CONDITIONS}
                    className={styles.link}
                  >
                    terms and conditions
                  </Link>{' '}
                  and{' '}
                  <Link
                    prefetch={false}
                    href={privacyPolicyUrl}
                    as={privacyPolicyUrl}
                    className={styles.link}
                  >
                    privacy policy
                  </Link>
                </span>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="continue-button-page-1"
              disabled={disabledSubmit}
              color="secondary"
              fullWidth
              size="large"
              variant="contained"
            >
              Register to continue
            </Button>
            <Button
              data-testid="register-sign-in"
              className={styles.gotoSignin}
              fullWidth
              size="large"
              color="secondary"
              variant="outlined"
              href={signInRedirectUrl}
            >
              Sign in with existing account
            </Button>
          </form>
        </div>
      </ProgressBarLayout>
    </>
  );
};

export default RegisterBasicInfo;
